import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Pagination from '../../../component/Admin/Pagination';
import { FaPlusCircle, FaEdit, FaTrashAlt, FaSearch, FaUniversity } from 'react-icons/fa';
import PaymentMethodForm from '../../../component/Admin/Payment/Form/PaymentMethodForm';
import Swal from 'sweetalert2';
import { canAccess } from '../../../helper/permissions';

const PaymentMethod = ({ data }) => {
    const header = {
        headers: { Authorization: `Bearer ${data.token}` },
    };
    const permissions = data?.user?.permissions || {};
    const canCreate = canAccess(permissions, 'payment_method', 'create');
    const canEdit = canAccess(permissions, 'payment_method', 'edit');
    const canDelete = canAccess(permissions, 'payment_method', 'delete');

    const [listPaymentMethod, setListPaymentMethod] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(12);
    const [search, setSearch] = useState('');
    const [openForm, setOpenForm] = useState(false);
    const [formID, setFormID] = useState(null);

    useEffect(() => {
        getListPaymentMethod();
        // eslint-disable-next-line
    }, [openForm]);

    const getListPaymentMethod = () => {
        axios
            .get(`api/payment-method`, header)
            .then((response) => {
                setListPaymentMethod(response.data || []);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            });
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return listPaymentMethod;
        return listPaymentMethod.filter((item) => {
            const bank = String(item.bank || '').toLowerCase();
            const rekening = String(item.rekening_number || '').toLowerCase();
            const owner = String(item.owner || '').toLowerCase();
            return bank.includes(q) || rekening.includes(q) || owner.includes(q);
        });
    }, [listPaymentMethod, search]);

    useEffect(() => {
        setPage(1);
    }, [search, limit]);

    const totalPages = Math.max(Math.ceil(filtered.length / limit), 1);
    const paginated = filtered.slice((page - 1) * limit, page * limit);

    const selectPage = (value) => setPage(value);

    const nextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    const prevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const limitPage = (event) => {
        setLimit(Number(event.target.value));
        setPage(1);
    };

    const openAdd = () => {
        setFormID(null);
        setOpenForm(true);
    };

    const handleClickForm = (paymentMethodID) => {
        axios
            .get(`api/payment-method/${paymentMethodID}`, header)
            .then((response) => {
                setFormID(response.data);
                setOpenForm(true);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            });
    };

    const closeForm = () => {
        setFormID(null);
        setOpenForm(false);
    };

    const handleDeletePaymentMethod = (paymentMethodID) => {
        Swal.fire({
            title: 'Hapus metode pembayaran?',
            text: 'Data yang dihapus tidak dapat dikembalikan.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#5C5E62',
            confirmButtonText: 'Ya, hapus',
            cancelButtonText: 'Batal',
        }).then((result) => {
            if (!result.isConfirmed) return;
            axios
                .delete(`api/payment-method/${paymentMethodID}`, header)
                .then(() => {
                    getListPaymentMethod();
                    Swal.fire({
                        title: 'Dihapus',
                        text: 'Metode pembayaran berhasil dihapus',
                        icon: 'success',
                        timer: 1200,
                        showConfirmButton: false,
                        confirmButtonColor: '#4569ab',
                    });
                })
                .catch((error) => {
                    console.error('Error deleting data:', error);
                    Swal.fire('Gagal', 'Terjadi kesalahan saat menghapus data.', 'error');
                });
        });
    };

    return (
        <>
            <div className="min-h-[50vh] space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="relative flex-1 max-w-md">
                        <FaSearch className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cari bank, rekening, pemilik..."
                            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 py-2.5 pl-9 pr-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                        />
                    </div>
                    <div className="flex items-center gap-2 sm:ml-auto">
                        <div className="inline-flex items-center gap-2 rounded-lg bg-primary/10 dark:bg-primary/20 px-3 py-2 text-sm font-bold text-primary dark:text-primary-on-dark">
                            <FaUniversity className="opacity-80" />
                            {filtered.length} Metode
                        </div>
                        {canCreate ? (
                            <button
                                type="button"
                                onClick={openAdd}
                                className="btn-primary !shadow-none"
                            >
                                <FaPlusCircle />
                                Tambah
                            </button>
                        ) : null}
                    </div>
                </div>

                {paginated.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {paginated.map((item) => (
                            <div
                                key={item.paymentMethodID}
                                className="group flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md hover:border-primary/40 transition-all duration-200 overflow-hidden"
                            >
                                <div className="flex items-start gap-3 px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700/80">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-on-dark">
                                        <FaUniversity className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0 flex-1 text-left">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                            Bank
                                        </p>
                                        <h3 className="truncate text-base font-bold text-gray-900 dark:text-white">
                                            {item.bank || '-'}
                                        </h3>
                                    </div>
                                </div>

                                <div className="flex-1 space-y-3 px-4 py-3 text-left">
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                            Nomor Rekening
                                        </p>
                                        <p className="font-mono text-sm font-semibold tracking-wide text-gray-800 dark:text-gray-200">
                                            {item.rekening_number || '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                            Pemilik
                                        </p>
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {item.owner || '-'}
                                        </p>
                                    </div>
                                </div>

                                {(canEdit || canDelete) ? (
                                    <div className="flex gap-2 border-t border-gray-100 dark:border-gray-700 px-4 py-3">
                                        {canEdit ? (
                                            <button
                                                type="button"
                                                onClick={() => handleClickForm(item.paymentMethodID)}
                                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary/10 px-3 py-2 text-xs font-bold text-primary hover:bg-primary hover:text-white dark:bg-primary/20 dark:text-primary-on-dark transition-colors"
                                            >
                                                <FaEdit />
                                                Edit
                                            </button>
                                        ) : null}
                                        {canDelete ? (
                                            <button
                                                type="button"
                                                onClick={() => handleDeletePaymentMethod(item.paymentMethodID)}
                                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-600 hover:text-white dark:bg-red-900/30 dark:text-red-400 transition-colors"
                                                title="Hapus"
                                                aria-label="Hapus"
                                            >
                                                <FaTrashAlt className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-14 text-center shadow-sm">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
                            <FaUniversity className="h-6 w-6 text-primary dark:text-primary-on-dark" />
                        </div>
                        <h3 className="mb-1 text-lg font-bold text-gray-800 dark:text-gray-100">
                            {search ? 'Metode tidak ditemukan' : 'Belum ada metode pembayaran'}
                        </h3>
                        <p className="mx-auto mb-5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                            {search
                                ? `Tidak ada hasil untuk “${search}”.`
                                : 'Tambahkan rekening bank untuk dipakai saat membuat LOA.'}
                        </p>
                        {!search && canCreate && (
                            <button
                                type="button"
                                onClick={openAdd}
                                className="btn-primary"
                            >
                                <FaPlusCircle />
                                Tambah Metode
                            </button>
                        )}
                    </div>
                )}

                {filtered.length > 0 && (
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        limit={limit}
                        limitPage={limitPage}
                        selectPage={selectPage}
                        nextPage={nextPage}
                        prevPage={prevPage}
                    />
                )}
            </div>

            <PaymentMethodForm
                openForm={openForm}
                closeForm={closeForm}
                data={formID}
                header={header}
            />
        </>
    );
};

export default PaymentMethod;
